// src/components/dashboard/FiltrosDashboard.tsx
import React, { useState, useEffect } from "react";
import Select, { MultiValue } from "react-select";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { ChevronUp, ChevronDown, Download } from "lucide-react";
import "../styles/dashboardStyle/FiltrosDashboard.css";
import { gerarPDFSlides } from "./dashboardPages/GerarPdf";
import { buildQueryParams, Filtros } from "../API/filterService";
import {
  getEstados,
  getMunicipios,
  getBairros,
  Estado,
  Municipio,
  Bairro,
  getFiltrosSelects,
  getEmpreendimentoMeta,
  PopularDashboardResponse,
  EmpreendimentoMetaResponse,
} from "../API/popularService";

type OptionType = { value: string; label: string };

interface FiltrosDashboardProps {
  planosSelecionados?: string[];
  onFiltrar?: (filtros: any) => void;
  mostrarBotaoRelatorio?: boolean;
}

const FiltrosDashboard: React.FC<FiltrosDashboardProps> = ({
  planosSelecionados = [],
  onFiltrar,
  mostrarBotaoRelatorio = false,
}) => {
  const [selectOptions, setSelectOptions] = useState<PopularDashboardResponse | null>(null);
  const [metaRanges, setMetaRanges] = useState<EmpreendimentoMetaResponse | null>(null);
  const [aberto, setAberto] = useState(true);
  const [mostrarMaisFiltros, setMostrarMaisFiltros] = useState(false);

  // ---------- Estados dos filtros ----------
  const [construtora, setConstrutora] = useState<MultiValue<OptionType>>([]);
  const [estado, setEstado] = useState<MultiValue<OptionType>>([]);
  const [municipio, setMunicipio] = useState<MultiValue<OptionType>>([]);
  const [bairro, setBairro] = useState<MultiValue<OptionType>>([]);

  const [quartos, setQuartos] = useState<string[]>([]);
  const [banheiros, setBanheiros] = useState<string[]>([]);
  const [suites, setSuites] = useState<string[]>([]);
  const [vagasGaragem, setVagasGaragem] = useState<string[]>([]);
  const [banheirosServico, setBanheirosServico] = useState<string[]>([]);
  const [quartoServico, setQuartoServico] = useState<string[]>([]);
  const [faseEmpreendimento, setFaseEmpreendimento] = useState<string[]>([]);

  const [totalVagasEmpreendimento, setTotalVagasEmpreendimento] = useState<[number, number]>([0, 10]);
  const [percentualVendido, setPercentualVendido] = useState<[number, number]>([0, 100]);
  const [percentualEstoque, setPercentualEstoque] = useState<[number, number]>([0, 100]);
  const [unidadesOfertadas, setUnidadesOfertadas] = useState<[number, number]>([0, 100]);
  const [unidadesVendidas, setUnidadesVendidas] = useState<[number, number]>([0, 100]);
  const [unidadesEstoque, setUnidadesEstoque] = useState<[number, number]>([0, 100]);
  const [quantidadeTorres, setQuantidadeTorres] = useState<[number, number]>([0, 100]);
  const [numeroElevadores, setNumeroElevadores] = useState<[number, number]>([0, 100]);
  const [numeroPavimentos, setNumeroPavimentos] = useState<[number, number]>([0, 100]);

  const [areaImovelMin, setAreaImovelMin] = useState<number | "">("");
  const [areaImovelMax, setAreaImovelMax] = useState<number | "">("");
  const [precoMedioMin, setPrecoMedioMin] = useState<number | "">("");
  const [precoMedioMax, setPrecoMedioMax] = useState<number | "">("");
  const [precoImovelMin, setPrecoImovelMin] = useState<number | "">("");
  const [precoImovelMax, setPrecoImovelMax] = useState<number | "">("");
  const [vgvMin, setVgvMin] = useState<number | "">("");
  const [vgvMax, setVgvMax] = useState<number | "">("");

  const [inicioComercializacao, setInicioComercializacao] = useState<string>("");
  const [entregaImovel, setEntregaImovel] = useState<string>("");

  const [segmento, setSegmento] = useState<MultiValue<OptionType>>([]);
  const [lazer, setLazer] = useState<MultiValue<OptionType>>([]);
  const [infraestrutura, setInfraestrutura] = useState<MultiValue<OptionType>>([]);
  const [seguranca, setSeguranca] = useState<MultiValue<OptionType>>([]);

  // ---------- Localização ----------
  const [estados, setEstados] = useState<Estado[]>([]);
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [bairros, setBairros] = useState<Bairro[]>([]);

  const [estadoSelecionado, setEstadoSelecionado] = useState<Estado | null>(null);
  const [municipioSelecionado, setMunicipioSelecionado] = useState<Municipio | null>(null);
  const [bairroSelecionado, setBairroSelecionado] = useState<Bairro | null>(null);

  const valorInternoMap: Record<string | number, number> = {
    "4+": 4,
    "3+": 3,
    Rotativa: 4,
  };

  // Mapeamento entre label do slider e a chave do metaRanges
  const rangeKeys: Record<string, { min: keyof EmpreendimentoMetaResponse; max: keyof EmpreendimentoMetaResponse }> = {
    "Pavimentos": { min: "pavimentos_min", max: "pavimentos_max" },
    "Elevadores": { min: "elevadores_min", max: "elevadores_max" },
    "Quantidade de torres": { min: "torres_min", max: "torres_max" },
    "Unidades ofertadas": { min: "ofertadas_min", max: "ofertadas_max" },
    "Unidades vendidas": { min: "vendidas_min", max: "vendidas_max" },
    "% Vendido": { min: "perc_vendido_min", max: "perc_vendido_max" },
    "Unidades em estoque": { min: "estoque_min", max: "estoque_max" },
    "% Estoque": { min: "perc_estoque_min", max: "perc_estoque_max" },
    "Vagas no empreendimento": { min: "vagas_min", max: "vagas_max" }
  };

  const filtrosDesabilitadosPorPlano: Record<string, string[]> = {
    compacto: ["Suítes", "Quarto de serviço", "Banheiro de serviço"],
    comercial: ["Suítes", "Quartos", "Banheiros", "Quarto de serviço", "Banheiro de serviço"],
    lote: [
      "Pavimentos",
      "Elevadores",
      "Quantidade de torres",
      "Unidades ofertadas",
      "Unidades vendidas",
      "% Vendido",
      "Unidades em estoque",
      "% Estoque",
      "Suítes",
      "Quartos",
      "Banheiros",
      "Quarto de serviço",
      "Banheiro de serviço",
    ],
  };

  const [loading, setLoading] = useState(false);

  // Carregar estados
  useEffect(() => {
    getEstados().then(setEstados).catch(console.error);
  }, []);

  // Carregar municípios
  useEffect(() => {
    if (estadoSelecionado) {
      getMunicipios(estadoSelecionado.id).then(setMunicipios).catch(console.error);
      setMunicipioSelecionado(null);
      setBairroSelecionado(null);
      setBairros([]);
    }
  }, [estadoSelecionado]);

  // Carregar bairros
  useEffect(() => {
    if (municipioSelecionado) {
      getBairros(municipioSelecionado.id).then(setBairros).catch(console.error);
      setBairroSelecionado(null);
    }
  }, [municipioSelecionado]);

  // Carregar filtros e metas da API
  useEffect(() => {
    const fetchFiltros = async () => {
      try {
        const selects = await getFiltrosSelects();
        const meta = await getEmpreendimentoMeta();

        setSelectOptions(selects);
        setMetaRanges(meta);
      } catch (error) {
        console.error("Erro ao carregar filtros:", error);
      }
    };
    fetchFiltros();
  }, []);

  // Filtros de range
  const filtrosRange = [
    { label: "Pavimentos", value: numeroPavimentos },
    { label: "Elevadores", value: numeroElevadores },
    { label: "Quantidade de torres", value: quantidadeTorres },
    { label: "Unidades ofertadas", value: unidadesOfertadas },
    { label: "Unidades vendidas", value: unidadesVendidas },
    { label: "% Vendido", value: percentualVendido },
    { label: "Unidades em estoque", value: unidadesEstoque },
    { label: "% Estoque", value: percentualEstoque },
    { label: "Vagas no empreendimento", value: totalVagasEmpreendimento },
  ];

  const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

  const clampRange = (val: [number, number], min: number, max: number): [number, number] => [
    clamp(val[0], min, max),
    clamp(val[1], min, max),
  ];

  useEffect(() => {
    if (metaRanges) {
      setNumeroPavimentos(clampRange([metaRanges.pavimentos_min, metaRanges.pavimentos_max], metaRanges.pavimentos_min, metaRanges.pavimentos_max));
      setNumeroElevadores(clampRange([metaRanges.elevadores_min, metaRanges.elevadores_max], metaRanges.elevadores_min, metaRanges.elevadores_max));
      setQuantidadeTorres(clampRange([metaRanges.torres_min, metaRanges.torres_max], metaRanges.torres_min, metaRanges.torres_max));
      setUnidadesOfertadas(clampRange([metaRanges.ofertadas_min, metaRanges.ofertadas_max], metaRanges.ofertadas_min, metaRanges.ofertadas_max));
      setUnidadesVendidas(clampRange([metaRanges.vendidas_min, metaRanges.vendidas_max], metaRanges.vendidas_min, metaRanges.vendidas_max));
      setPercentualVendido(clampRange([metaRanges.perc_vendido_min, metaRanges.perc_vendido_max], metaRanges.perc_vendido_min, metaRanges.perc_vendido_max));
      setUnidadesEstoque(clampRange([metaRanges.estoque_min, metaRanges.estoque_max], metaRanges.estoque_min, metaRanges.estoque_max));
      setPercentualEstoque(clampRange([metaRanges.perc_estoque_min, metaRanges.perc_estoque_max], metaRanges.perc_estoque_min, metaRanges.perc_estoque_max));
      setTotalVagasEmpreendimento(clampRange([metaRanges.vagas_min, metaRanges.vagas_max], metaRanges.vagas_min, metaRanges.vagas_max));
    }
  }, [metaRanges]);

  // Toggle checkbox
  const toggleCheckbox = (item: string, list: string[], setList: React.Dispatch<React.SetStateAction<string[]>>) => {
    if (list.includes(item)) {
      setList(list.filter((i) => i !== item));
    } else {
      setList([...list, item]);
    }
  };

  // Map da API para Select
  const mapOptions = (items: { id: string; nome: string }[]) =>
    items.map(item => ({ value: item.id, label: item.nome }));


  // Disabilitar filtros
  const isDisabled = (filtroLabel: string) => {
    // Se não há plano selecionado, desabilita todos
      if (!planosSelecionados.length) return true;

      // percorre todos os planos selecionados
      // só desabilita se todos os planos selecionados tiverem o filtro desabilitado
      return planosSelecionados.every(
        (p) => filtrosDesabilitadosPorPlano[p.toLowerCase()]?.includes(filtroLabel)
      );
  };

  // Filtrar
  const handleFiltrar = async () => {
    setLoading(true);
    try {
      const filtros: Filtros = {};

    // Quartos / Suítes / Banheiros
    // Quartos / Suítes / Banheiros
    if (quartos.length > 0) filtros.quartos = quartos.map(q => q.toString());
    if (suites.length > 0) filtros.suites = suites.map(s => s.toString());
    if (banheiros.length > 0) filtros.banheiros = banheiros.map(b => b.toString());
    if (quartoServico.length > 0) filtros.quarto_servico = quartoServico.map(qs => qs.toString());
    if (banheirosServico.length > 0) filtros.banheiro_servico = banheirosServico.map(bs => bs.toString());
    if (vagasGaragem.length > 0) filtros.vagas_por_unidade = vagasGaragem.map(vu => vu.toString());
    
    // Sliders (ranges) com min e max
    if (numeroPavimentos.some((v) => v > 0)) {
      filtros.pavimentos_min = numeroPavimentos[0];
      filtros.pavimentos_max = numeroPavimentos[1];
    }
    if (numeroElevadores.some((v) => v > 0)) {
      filtros.elevadores_min = numeroElevadores[0];
      filtros.elevadores_max = numeroElevadores[1];
    }
    if (quantidadeTorres.some((v) => v > 0)) {
      filtros.torres_min = quantidadeTorres[0];
      filtros.torres_max = quantidadeTorres[1];
    }

    if (unidadesOfertadas.some((v) => v > 0)) {
      filtros.unidades_ofertadas_min = unidadesOfertadas[0];
      filtros.unidades_ofertadas_max = unidadesOfertadas[1];
    }

    if (unidadesVendidas.some((v) => v > 0)) {
      filtros.unidades_vendidas_min = unidadesVendidas[0];
      filtros.unidades_vendidas_max = unidadesVendidas[1];
    }

    if (percentualVendido.some((v) => v > 0)) {
      filtros.percentual_vendido_min = percentualVendido[0];
      filtros.percentual_vendido_max = percentualVendido[1];
    }

    if (unidadesEstoque.some((v) => v > 0)) {
      filtros.unidades_estoque_min = unidadesEstoque[0];
      filtros.unidades_estoque_max = unidadesEstoque[1];
    }

    if (percentualEstoque.some((v) => v > 0)) {
      filtros.percentual_estoque_min = percentualEstoque[0];
      filtros.percentual_estoque_max = percentualEstoque[1];
    }

    if (totalVagasEmpreendimento.some((v) => v > 0)) {
      filtros.vagas_empreendimento_min = totalVagasEmpreendimento[0];
      filtros.vagas_empreendimento_max = totalVagasEmpreendimento[1];
    }

    // Inputs numéricos
    if (areaImovelMin !== "") filtros.area_min = Number(areaImovelMin);
    if (areaImovelMax !== "") filtros.area_max = Number(areaImovelMax);
    if (precoImovelMin !== "") filtros.preco_min = Number(precoImovelMin);
    if (precoImovelMax !== "") filtros.preco_max = Number(precoImovelMax);
    if (precoMedioMin !== "") filtros.preco_medio_min = Number(precoMedioMin);
    if (precoMedioMax !== "") filtros.preco_medio_max = Number(precoMedioMax);
    if (vgvMin !== "") filtros.vgv_min = Number(vgvMin);
    if (vgvMax !== "") filtros.vgv_max = Number(vgvMax);

    // Datas
    if (inicioComercializacao) filtros.inicio_vendas_de = `${inicioComercializacao}-01`;
    if (entregaImovel) filtros.entrega_de = `${entregaImovel}-01`;

    // Multiselects: envia id, mostra label
    if (lazer.length > 0) filtros.lazer = lazer.map((l) => Number(l.value));
    if (infraestrutura.length > 0) filtros.infraestrutura = infraestrutura.map((i) => Number(i.value));
    if (seguranca.length > 0) filtros.seguranca = seguranca.map((s) => Number(s.value));
    if (construtora.length > 0) filtros.incorporadora = construtora.map(c => Number(c.value));
    if (segmento.length > 0) filtros.segmento = segmento.map((s) => Number(s.value));
    if (faseEmpreendimento.length > 0) filtros.fase_empreendimento = faseEmpreendimento;


    // Localização
    if (estadoSelecionado) filtros.estado = estadoSelecionado.sigla;
    if (municipioSelecionado) {
      filtros.municipio = [
        municipioSelecionado.nome.toLowerCase()
      ];
    }

    if (bairroSelecionado) {
      filtros.bairro = [
        bairroSelecionado.nome.toLowerCase()
      ];
    }

    const query = buildQueryParams(filtros);
    console.log("Query pronta:", query);
    if (onFiltrar) {
      await onFiltrar(filtros); // se onFiltrar retornar uma Promise, espere ela
    }
      } catch (error) {
      console.error("Erro ao filtrar:", error);
    } finally {
      setLoading(false);
    }

  };


  return (
    <div className="filtros-dashboard-card">
      <div className="container-header-dashboard">
        <div className="filtros-toggle-dashboard" onClick={() => setAberto(!aberto)} role="button" tabIndex={0}>
          {aberto ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          <span>{aberto ? "Ocultar filtros" : "Mostrar filtros"}</span>
        </div>
        {mostrarBotaoRelatorio && (
          <button onClick={gerarPDFSlides} className="botao-relatorio">
            <Download size={16} />
            Baixar relatório em PDF
          </button>
        )}
      </div>

      {aberto && (
        <>
          <div className="grid-filtros">

            {/* Localização */}
            <div className="filtro-item">
              <label>Estado</label>
              <Select
                placeholder="Selecione o Estado"
                value={estadoSelecionado ? { value: estadoSelecionado.id, label: estadoSelecionado.nome } : null}
                options={estados.map(e => ({ value: e.id, label: `${e.nome}` }))}
                onChange={(option) => setEstadoSelecionado(estados.find(e => e.id === option?.value) || null)}
              />
            </div>

            <div className="filtro-item">
              <label>Município</label>
              <Select
                placeholder="Selecione o Município"
                value={municipioSelecionado ? { value: municipioSelecionado.id, label: municipioSelecionado.nome_original } : null}
                options={municipios.map(m => ({ value: m.id, label: m.nome_original }))}
                onChange={(option) => setMunicipioSelecionado(municipios.find(m => m.id === option?.value) || null)}
                isDisabled={!estadoSelecionado}
              />
            </div>

            <div className="filtro-item">
              <label>Bairro</label>
              <Select
                placeholder="Selecione o Bairro"
                value={bairroSelecionado ? { value: bairroSelecionado.id, label: bairroSelecionado.nome_original } : null}
                options={bairros.map(b => ({ value: b.id, label: b.nome_original }))}
                onChange={(option) => setBairroSelecionado(bairros.find(b => b.id === option?.value) || null)}
                isDisabled={!municipioSelecionado}
              />
            </div>

            {/* Construtora */}
            <div className="filtro-item">
              <label>Construtora</label>
              <Select
                isMulti
                isSearchable
                value={construtora}
                onChange={setConstrutora}
                options={selectOptions ? mapOptions(selectOptions.incorporadoras) : []}
              />
            </div>

            {/* Segmento */}
            <div className="filtro-item">
              <label>Segmento</label>
              <Select
                isMulti
                isSearchable
                value={segmento}
                onChange={setSegmento}
                options={selectOptions ? mapOptions(selectOptions.segmentos) : []}
              />
            </div>

            {/* Datas */}
            <div className="filtro-item filtro-date">
              <label>Início das vendas</label>
              <input type="month" value={inicioComercializacao} onChange={(e) => setInicioComercializacao(e.target.value)} />
            </div>
            <div className="filtro-item filtro-date">
              <label>Entrega do imóvel</label>
              <input type="month" value={entregaImovel} onChange={(e) => setEntregaImovel(e.target.value)} />
            </div>

            {/* Etapa */}
            <div className="filtro-item">
              <label>Etapa</label>
              <Select
                isMulti
                isSearchable
                options={[{ value: "Planta", label: "Planta" }, { value: "Obra", label: "Obra" }, { value: "Pronto", label: "Pronto" }]}
                value={faseEmpreendimento.map(g => ({ value: g, label: g }))}
                onChange={(selected) => setFaseEmpreendimento(selected ? selected.map(s => s.value) : [])}
              />
            </div>

            {/* Lazer, Infraestrutura, Segurança */}
            <div className="filtro-item">
              <label>Lazer</label>
              <Select isMulti value={lazer} onChange={setLazer} options={selectOptions ? mapOptions(selectOptions.lazer) : []} />
            </div>
            <div className="filtro-item">
              <label>Infraestrutura</label>
              <Select isMulti value={infraestrutura} onChange={setInfraestrutura} options={selectOptions ? mapOptions(selectOptions.infraestrutura) : []} />
            </div>
            <div className="filtro-item">
              <label>Segurança</label>
              <Select isMulti value={seguranca} onChange={setSeguranca} options={selectOptions ? mapOptions(selectOptions.seguranca) : []} />
            </div>
          </div>

          {/* Mais filtros */}
          <div className="mais-filtros">
            <div className="collapse-header-mais-filtros" onClick={() => setMostrarMaisFiltros(!mostrarMaisFiltros)}>
              {mostrarMaisFiltros ? <ChevronUp /> : <ChevronDown />}
              <strong>Mais filtros</strong>
            </div>
            {mostrarMaisFiltros && (
              <div className="mais-filtros-grid-container">
                <div className="mais-filtros-grid">
                {/* Range Sliders */}
                {filtrosRange.map((r) => {
                const key = rangeKeys[r.label];

                const setValue = (val: [number, number]) => {
                  switch (r.label) {
                    case "Pavimentos": setNumeroPavimentos(val); break;
                    case "Elevadores": setNumeroElevadores(val); break;
                    case "Quantidade de torres": setQuantidadeTorres(val); break;
                    case "Unidades ofertadas": setUnidadesOfertadas(val); break;
                    case "Unidades vendidas": setUnidadesVendidas(val); break;
                    case "% Vendido": setPercentualVendido(val); break;
                    case "Unidades em estoque": setUnidadesEstoque(val); break;
                    case "% Estoque": setPercentualEstoque(val); break;
                    case "Vagas no empreendimento": setTotalVagasEmpreendimento(val); break;
                  }
                };
              
                const min = metaRanges ? metaRanges[key.min] ?? 0 : 0;
                const max = metaRanges ? metaRanges[key.max] ?? 100 : 100;
                const value = clampRange(r.value as [number, number], min, max);

                return (
                  <div key={r.label} className="filtro-item">
                    <label>{r.label}</label>
                    <Slider
                      range
                      min={min}
                      max={max}
                      value={value}
                      onChange={(val) => setValue(clampRange(val as [number, number], min, max))}
                      disabled={isDisabled(r.label)}
                    />
                    <div className="filtro-range-values">
                      {value[0]} - {value[1]} {r.label.includes("%") ? "%" : "unidades"}
                    </div>
                  </div>
                );
              })}

                  {/* Inputs numéricos */}
                  {[
                    { label: "Área do imóvel (m²)", min: areaImovelMin, max: areaImovelMax, setMin: setAreaImovelMin, setMax: setAreaImovelMax },
                    { label: "Preço do imóvel", min: precoImovelMin, max: precoImovelMax, setMin: setPrecoImovelMin, setMax: setPrecoImovelMax },
                    { label: "Preço médio m²", min: precoMedioMin, max: precoMedioMax, setMin: setPrecoMedioMin, setMax: setPrecoMedioMax },
                    { label: "VGV", min: vgvMin, max: vgvMax, setMin: setVgvMin, setMax: setVgvMax },
                  ].map((i) => (
                    <div key={i.label} className="filtro-item">
                      <label>{i.label}</label>
                      <div className="filtro-range">
                        <input type="number" placeholder="Mín" className="filtro-input" value={i.min} onChange={(e) => i.setMin(e.target.value ? Number(e.target.value) : "")} />
                        <span className="filtro-separador">-</span>
                        <input type="number" placeholder="Máx" className="filtro-input" value={i.max} onChange={(e) => i.setMax(e.target.value ? Number(e.target.value) : "")} />
                      </div>
                    </div>
                  ))}

                  {/* Checkboxes */}
                  {[
                    { label: "Suítes", values: suites, setValues: setSuites, options: [0, 1, 2, 3, "4+"] },
                    { label: "Quartos", values: quartos, setValues: setQuartos, options: [0, 1, 2, 3, "4+"] },
                    { label: "Banheiros", values: banheiros, setValues: setBanheiros, options: [0, 1, 2, 3, "4+"] },
                    { label: "Quarto de serviço", values: quartoServico, setValues: setQuartoServico, options: [0, 1, 2, "3+"] },
                    { label: "Banheiro de serviço", values: banheirosServico, setValues: setBanheirosServico, options: [0, 1, 2, "3+"] },
                    { label: "Vagas por unidade", values: vagasGaragem, setValues: setVagasGaragem, options: [0, 1, 2, "3+", "Rotativa"] },
                  ].map((c) => (
                    <div key={c.label} className="filtro-item">
                      <label>{c.label}</label>
                      <div className="checkbox-group">
                        {c.options.map((o) => (
                          <label key={o}>
                            <input
                              type="checkbox"
                              checked={c.values.includes(String(valorInternoMap[o] ?? o))}
                              onChange={() =>
                                toggleCheckbox(String(valorInternoMap[o] ?? o), c.values, c.setValues)
                              }
                              disabled={isDisabled((c.label))}
                            />
                            {o}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Botão Filtrar */}
          <div className="botao-container">
            <button
              className={`botao-filtrar ${loading ? "loading" : ""}`}
              onClick={handleFiltrar}
              disabled={loading} // desabilita enquanto carrega
            >
              {loading ? "Filtrando..." : "Filtrar"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FiltrosDashboard;
